import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useLocation, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import { useScrollToSection } from "../../../app/useScrollToSection";
import { getParamAsInt } from "../../../app/utils";
import { AlertLevel } from "../../../components/AlertMessage";
import ButtonWithConfirm from "../../../components/ButtonWithConfirm";
import { SelectDevelopmentView } from "../../../components/input/SelectDevelopmentView";
import RtkqResultViewer from "../../../components/RtkqResultViewer";
import Messages from "../../messages/Messages";
import PromptPanel from "../../prompts/PromptPanel";
import AutosaveMessage, { AutosaveStatus } from "../AutosaveMessage";

import Organizer from "./Organizer";
import { serializeOrganizer } from "./organizerHelper";
import {
	useOrganizeQuery,
	useSaveOrganizerMutation,
	OrganizerValidationResponse,
	SaveOrganizerModel,
} from "./organizingApiSlice";

export default function Organize() {
	const [queryParams] = useSearchParams();
	const draftId = getParamAsInt(queryParams, "draftId");

	const [view, setView] = useState<string>("legacy");
	const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>(AutosaveStatus.Disabled);

	const navigate = useNavigate();

	const result = useOrganizeQuery(draftId ?? skipToken);
	const data = result.data;

	const [postSaveOrganizer, saveOrganizerResult] = useSaveOrganizerMutation();

	useScrollToSection();

	useEffect(() => {
		if (!data) return;
		if (!data.autosaveIntervalSeconds) return;
		setAutosaveStatus(AutosaveStatus.Waiting);

		const interval = setInterval(() => {
			autosaveOrganizer();
		}, data.autosaveIntervalSeconds * 1000);

		return () => {
			setAutosaveStatus(AutosaveStatus.Disabled);
			clearInterval(interval);
		};
	}, [data]);

	function saveOrganizer(
		onSuccess: (payload: OrganizerValidationResponse) => void,
		onError: (error: any) => void
	) {
		if (!draftId) return;

		const serial = serializeOrganizer();
		if (serial) {
			const model: SaveOrganizerModel = {
				draftId: draftId,
				organizerResponse: JSON.stringify(serial),
			};
			setAutosaveStatus(AutosaveStatus.Saving);
			postSaveOrganizer(model)
				.unwrap()
				.then((payload) => onSuccess(payload))
				.catch((error) => onError(error));
		}
	}

	function autosaveOrganizer() {
		const onSuccess = (payload: OrganizerValidationResponse) => {
			setAutosaveStatus(AutosaveStatus.Waiting);
		};
		const onError = (error: any) => {
			setAutosaveStatus(AutosaveStatus.Error);
			console.log(error);
		};

		saveOrganizer(onSuccess, onError);
	}

	function handleSaveAndQuit(event: React.MouseEvent<HTMLButtonElement>) {
		const onSuccess = () => {
			setAutosaveStatus(AutosaveStatus.Disabled);
			navigate("/home");
		};
		const onError = () => {
			setAutosaveStatus(AutosaveStatus.Error);
		};

		saveOrganizer(onSuccess, onError);
	}

	function handlePrevious() {
		const onSuccess = () => {
			setAutosaveStatus(AutosaveStatus.Disabled);
			navigate(`/writing/organizers?draftId=${draftId}`);
		};
		const onError = () => {
			setAutosaveStatus(AutosaveStatus.Error);
		};

		saveOrganizer(onSuccess, onError);
	}

	function handleNext() {
		const onSuccess = () => {
			setAutosaveStatus(AutosaveStatus.Disabled);
			navigate(`/writing/write?draftId=${draftId}`);
		};
		const onError = () => {
			setAutosaveStatus(AutosaveStatus.Error);
		};

		saveOrganizer(onSuccess, onError);
	}

	if (draftId === undefined) return null;

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Organize Your Writing!</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "legacy" && (
				<>
					{result.isSuccess && data && (
						<>
							<PromptPanel
								name={data.promptName}
								showName={true}
								renderedText={data.renderedPromptText}
								stimulusMaterials={data.stimulusMaterials}
								showGenre={false}></PromptPanel>
							<h2 className="mt-4">{data.name}</h2>
							<Organizer
								className="pt-2 relative"
								html={data.html}
								response={data.response}
								readonly={false}
							/>

							<AutosaveMessage
								onClick={autosaveOrganizer}
								status={autosaveStatus}
								interval={data.autosaveIntervalSeconds}
								className="transition-opacity ease-in duration-1000 mt-4"
							/>

							<div className="flex justify-between mt-4">
								<div>
									<ButtonWithConfirm
										id="change-organizer-button"
										className="btn btn-neutral"
										message="You have already chosen an organizer. Do you wish to switch organizers and lose all previous organizer data?"
										type={AlertLevel.Warning}
										confirmLabel="Yes"
										cancelLabel="Cancel"
										onConfirm={handlePrevious}>
										Change Organizer
									</ButtonWithConfirm>
								</div>
								<div>
									<button
										id="save-and-quit-organizer-button"
										className="btn btn-neutral mr-2"
										onClick={handleSaveAndQuit}>
										Save and Quit
									</button>
									<button
										id="go-to-writing-button"
										className="btn btn-alert"
										onClick={handleNext}>
										{data.draftStatus == ""
											? "Start Writing"
											: "Continue Writing"}
									</button>
								</div>
							</div>
							<hr className="my-4" />
							<section id="EssayMessages">
								<Messages draftId={data.draftId} />
							</section>
						</>
					)}
				</>
			)}
			{view == "raw" && <RtkqResultViewer heading="Organize" result={result} />}
		</section>
	);
}
